import { TFunction } from 'i18next';
import type { TeamNode } from './components/FilterDrawer';
import type { NewReportData } from './types';

const DIRECT_REPORTS_PREFIX = 'direct:';

const getDirectReportsId = (teamId: string): string => `${DIRECT_REPORTS_PREFIX}${teamId}`;

export const parseDirectReportsId = (teamId: string): string | null =>
  teamId.startsWith(DIRECT_REPORTS_PREFIX) ? teamId.slice(DIRECT_REPORTS_PREFIX.length) : null;

const toTeamId = (id: string | number): string => String(id);

const getTeamTreeDepth = (node: TeamNode): number => {
  if (node.isDirectReports || node.children.length === 0) {
    return 0;
  }
  return 1 + Math.max(...node.children.map(getTeamTreeDepth));
};

const getDescendantTeamCount = (node: TeamNode): number =>
  node.children.reduce((sum, child) => sum + 1 + getDescendantTeamCount(child), 0);

const sortTeamNodes = (nodes: TeamNode[]): TeamNode[] =>
  [...nodes]
    .map((node) => ({
      ...node,
      children: node.isDirectReports ? node.children : sortTeamNodes(node.children),
    }))
    .sort((a, b) => {
      if (a.isDirectReports !== b.isDirectReports) {
        return a.isDirectReports ? -1 : 1;
      }

      const aHasChildren = a.children.length > 0;
      const bHasChildren = b.children.length > 0;

      if (aHasChildren !== bHasChildren) {
        return aHasChildren ? -1 : 1;
      }

      if (aHasChildren && bHasChildren) {
        const depthDiff = getTeamTreeDepth(b) - getTeamTreeDepth(a);
        if (depthDiff !== 0) {
          return depthDiff;
        }

        const countDiff = getDescendantTeamCount(b) - getDescendantTeamCount(a);
        if (countDiff !== 0) {
          return countDiff;
        }
      }

      return a.name.localeCompare(b.name);
    });

const buildTeamNode = (teamId: string, teamsById: Map<string, NewReportData['teams'][0]>, t: TFunction): TeamNode => {
  const team = teamsById.get(teamId);
  if (!team) {
    return { id: teamId, name: teamId, children: [] };
  }

  const memberTeamIds = team.memberTeams ?? [];
  const subteams = memberTeamIds.map((childId) => buildTeamNode(toTeamId(childId), teamsById, t));
  const children =
    memberTeamIds.length > 0
      ? [
          {
            id: getDirectReportsId(toTeamId(team.id)),
            name: t('directMembers'),
            children: [],
            isDirectReports: true,
          },
          ...sortTeamNodes(subteams),
        ]
      : [];

  return {
    id: toTeamId(team.id),
    name: team.teamName || t('withoutSupervisor'),
    children,
  };
};

export const buildTeamNodes = (data: NewReportData, t: TFunction): TeamNode[] => {
  const teamsById = new Map(data.teams.map((team) => [toTeamId(team.id), team]));

  return sortTeamNodes(data.reportRoots.map((rootId) => buildTeamNode(toTeamId(rootId), teamsById, t)));
};
